<template>
  <v-main class="student-lesson">
    <div v-if="!ready" class="student-lesson__loading">
      <v-progress-circular class="theme-loader" size="64" width="5" indeterminate />
      <p>Завантажуємо урок…</p>
    </div>

    <div v-else class="student-lesson__inner">
      <button
        v-if="isParentPreview && returnSource && returnSource !== 'parent-course'"
        type="button"
        class="student-lesson__context-back"
        @click="returnToSource"
      >
        <v-icon size="18">mdi-arrow-left</v-icon>
        {{ returnLabel }}
      </button>
      <nav class="student-lesson__breadcrumb" aria-label="Навігація сторінки">
        <button
          type="button"
          class="student-lesson__breadcrumb-course"
          :class="{
            'student-lesson__breadcrumb-course--parent': isParentPreview,
          }"
          title="Повернутися до списку всіх уроків"
          @click="goToCourse"
        >
          <v-icon size="18">mdi-arrow-left</v-icon>
          {{ courseName }}
        </button>
        <span aria-hidden="true">/</span>
        <span
          class="student-lesson__breadcrumb-current"
          aria-current="page"
        >
          {{ nameLesson }}
        </span>
      </nav>

      <section class="student-lesson__hero">
        <div class="student-lesson__identity">
          <span class="student-lesson__icon">
            <v-icon color="#2d68bd" size="30"
              >mdi-book-open-page-variant-outline</v-icon
            >
          </span>
          <div>
            <span class="student-lesson__eyebrow"
              >{{ courseName
              }}<template v-if="!isGuest && grade">
                · {{ grade }}-й клас</template
              ></span
            >
            <h1>
              <button
                type="button"
                class="student-lesson__title-link"
                title="Повернутися до списку всіх уроків"
                @click="goToCourse"
              >
                {{ nameLesson }}
              </button>
            </h1>
            <div class="student-lesson__badges">
              <span v-if="isParentPreview" class="student-lesson__parent-badge">Перегляд для батьків</span>
              <span v-if="evaluationResult" class="student-lesson__grade"
                >Оцінка {{ evaluationResult }}</span
              >
              <span
                class="student-lesson__status"
                :class="`student-lesson__status--${statusTheme}`"
                >{{ isLessonPreview ? 'Повна програма' : statusLabel }}</span
              >
            </div>
          </div>
        </div>

        <div class="student-lesson__contact">
          <div
            class="student-lesson__teacher"
            v-if="isGuest || teacherName || teacher.email"
          >
            <span><v-icon size="22">mdi-account-outline</v-icon></span>
            <div>
              <small>{{ isGuest ? 'Викладач' : 'Вчитель' }}</small>
              <strong>{{ isGuest ? 'Команда RedFox' : teacherName || 'Не вказано' }}</strong>
              <span v-if="isGuest" class="student-lesson__teacher-demo">
                Демонстраційний профіль
              </span>
              <a
                v-else-if="teacher.email"
                :href="`mailto:${teacher.email}?subject=${courseName}, ${nameLesson}`"
                >{{ teacher.email }}</a
              >
            </div>
          </div>
          <button v-if="!isParentPreview && !isLessonPreview" type="button" class="student-lesson__chat" @click="openChat">
            <span
              v-if="hasUnreadMessages"
              class="student-lesson__unread"
            ></span>
            <v-icon size="20">mdi-message-outline</v-icon>
            Чат
          </button>
          <button
            type="button"
            class="student-lesson__course-content"
            @click="openLessonDrawer"
          >
            <v-icon size="20">mdi-format-list-bulleted</v-icon>
            Зміст курсу
          </button>
        </div>
      </section>

      <nav
        v-if="lessons.length"
        class="student-lesson__pager"
        aria-label="Навігація між уроками"
      >
        <button
          type="button"
          :disabled="!previousLesson"
          @click="navigateLesson(previousLesson)"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
          <span>
            <small>Попередній урок <em v-if="isPreviewLesson(previousLesson)">Повна програма</em></small>
            {{ previousLessonName }}
          </span>
        </button>
        <button
          type="button"
          :disabled="!nextLesson"
          @click="navigateLesson(nextLesson)"
        >
          <span>
            <small>Наступний урок <em v-if="isPreviewLesson(nextLesson)">Повна програма</em></small>
            {{ nextLessonName }}
          </span>
          <v-icon size="20">mdi-arrow-right</v-icon>
        </button>
      </nav>

      <article v-if="!isLessonPreview" class="student-lesson__content-card">
        <div
          ref="lessonPage"
          class="student-lesson__content"
          v-html="lessonText"
        ></div>

        <div
          v-if="!isParentPreview && !showEvaluation && needShowChangeStatus"
          class="student-lesson__complete"
        >
          <span><v-icon size="25">mdi-check-circle-outline</v-icon></span>
          <div>
            <strong>Урок зрозумілий?</strong>
            <p>Познач його як вивчений, щоб перейти далі.</p>
          </div>
          <button type="button" @click="changeLessonStatus">Вивчив</button>
        </div>
      </article>

      <section v-else class="student-lesson__preview-card">
        <button
          type="button"
          class="student-lesson__preview-trigger"
          aria-label="Залишити контакти"
          @click="requestFullAccess"
        >
          <span class="student-lesson__preview-icon">
            <v-icon size="34">mdi-lock-open-outline</v-icon>
          </span>
          <span class="student-lesson__preview-label">Залишити контакти</span>
        </button>
        <h2>{{ nameLesson }}</h2>
        <p>
          Цей урок входить до повної програми
          <template v-if="grade"> {{ grade }} класу</template>.
          Залиште контакти — команда RedFox зв’яжеться з Вами.
        </p>
        <button type="button" class="student-lesson__preview-submit" @click="requestFullAccess">
          Залишити контакти
          <v-icon color="#fff" size="20">mdi-arrow-right</v-icon>
        </button>
        <button
          v-if="availableDemoLesson"
          type="button"
          class="student-lesson__preview-back"
          @click="navigateLesson(availableDemoLesson)"
        >
          Повернутися до доступного демо-уроку
        </button>
      </section>

      <section
        v-for="item in isLessonPreview ? [] : videoLinks"
        :key="item.name"
        class="student-lesson__video-card"
      >
        <h2>{{ item.name }}</h2>
        <div class="student-lesson__video">
          <iframe
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
            frameborder="0"
            :src="item.link"
            :title="item.name"
          ></iframe>
        </div>
      </section>

      <section v-if="!isParentPreview && !isLessonPreview && showEvaluation" class="student-lesson__evaluation">
        <header>
          <h2>{{ isExam ? 'Контрольна робота' : 'Завдання до уроку' }}</h2>
          <p>Виконай завдання та надішли відповіді на перевірку.</p>
        </header>
        <EvaluationDeck :isExam="isExam" :evaluationDeck="evaluationDeck" />
      </section>

      <nav
        v-if="lessons.length"
        class="student-lesson__pager student-lesson__pager--bottom"
        aria-label="Навігація між уроками"
      >
        <button
          type="button"
          :disabled="!previousLesson"
          @click="navigateLesson(previousLesson)"
        >
          <v-icon size="20">mdi-arrow-left</v-icon>
          <span>
            <small>Попередній урок <em v-if="isPreviewLesson(previousLesson)">Повна програма</em></small>
            {{ previousLessonName }}
          </span>
        </button>
        <button
          type="button"
          :disabled="!nextLesson"
          @click="navigateLesson(nextLesson)"
        >
          <span>
            <small>Наступний урок <em v-if="isPreviewLesson(nextLesson)">Повна програма</em></small>
            {{ nextLessonName }}
          </span>
          <v-icon size="20">mdi-arrow-right</v-icon>
        </button>
      </nav>

      <div
        class="student-lesson__drawer-backdrop"
        :class="{ 'student-lesson__drawer-backdrop--open': lessonDrawerOpen }"
        @click="closeLessonDrawer"
      ></div>
      <aside
        class="student-lesson__drawer"
        :class="{ 'student-lesson__drawer--open': lessonDrawerOpen }"
        :aria-hidden="String(!lessonDrawerOpen)"
        aria-label="Зміст курсу"
      >
        <header class="student-lesson__drawer-header">
          <div>
            <span>Зміст курсу</span>
            <h2>{{ courseName }}</h2>
            <p>{{ lessons.length }} уроків</p>
          </div>
          <button type="button" aria-label="Закрити" @click="closeLessonDrawer">
            <v-icon color="#667085" size="21">mdi-close</v-icon>
          </button>
        </header>

        <label class="student-lesson__drawer-search">
          <v-icon color="#7a8592" size="20">mdi-magnify</v-icon>
          <input
            v-model.trim="lessonSearch"
            type="search"
            placeholder="Знайти урок"
          />
          <button
            v-if="lessonSearch"
            type="button"
            aria-label="Очистити пошук"
            @click="lessonSearch = ''"
          >
            <v-icon color="#7a8592" size="18">mdi-close</v-icon>
          </button>
        </label>

        <div
          v-if="isGuest"
          class="student-lesson__drawer-filters"
          role="group"
          aria-label="Фільтр доступності уроків"
        >
          <button
            v-for="filter in drawerAccessFilters"
            :key="filter.value"
            type="button"
            :class="{
              'student-lesson__drawer-filter--active':
                drawerAccessFilter === filter.value,
            }"
            @click="drawerAccessFilter = filter.value"
          >
            {{ filter.label }}
            <small>{{ drawerLessonCount(filter.value) }}</small>
          </button>
        </div>

        <div class="student-lesson__drawer-list">
          <button
            v-for="lesson in filteredDrawerLessons"
            :key="lesson.id"
            type="button"
            class="student-lesson__drawer-lesson"
            :class="{
              'student-lesson__drawer-lesson--active': isCurrentLesson(lesson),
              'student-lesson__drawer-lesson--preview': isPreviewLesson(lesson),
            }"
            @click="selectDrawerLesson(lesson)"
          >
            <span class="student-lesson__drawer-number">{{
              lesson.index + 1
            }}</span>
            <span class="student-lesson__drawer-name">
              <strong>{{ lesson.name }}</strong>
              <small
                class="student-lesson__drawer-availability"
                :class="{
                  'student-lesson__drawer-availability--demo': lesson.isDemo,
                }"
              >
                {{
                  isGuest
                    ? lesson.isDemo
                      ? 'Доступний у демо'
                      : 'Повна програма'
                    : lesson.statusLabel || 'Не розпочато'
                }}
              </small>
            </span>
            <v-icon color="inherit" size="19">
              {{
                isCurrentLesson(lesson)
                  ? 'mdi-bookmark'
                  : isPreviewLesson(lesson)
                  ? 'mdi-lock-outline'
                  : 'mdi-chevron-right'
              }}
            </v-icon>
          </button>
          <div
            v-if="!filteredDrawerLessons.length"
            class="student-lesson__drawer-empty"
          >
            <v-icon color="#98a2b3" size="28">mdi-magnify-close</v-icon>
            <strong>Уроків не знайдено</strong>
            <span>Спробуйте змінити запит.</span>
          </div>
        </div>
      </aside>

      <transition name="student-scroll-top">
        <button
          v-if="showScrollTop && !lessonDrawerOpen"
          type="button"
          class="student-lesson__scroll-top"
          aria-label="Повернутися на початок сторінки"
          title="Нагору"
          @click="scrollToTop"
        >
          <v-icon size="24">mdi-arrow-up</v-icon>
        </button>
      </transition>

      <v-snackbar
        v-model="demoLessonNotice"
        class="student-lesson__demo-toast"
        bottom
        :timeout="10000"
      >
        <div class="student-lesson__demo-toast-content">
          <span><v-icon size="23">mdi-information-outline</v-icon></span>
          <div>
            <strong>Ви переглядаєте демо-урок</strong>
            <p>Повна програма містить усі уроки, завдання та навчальні матеріали.</p>
            <label class="student-lesson__demo-toast-preference">
              <input
                v-model="suppressDemoLessonNotice"
                type="checkbox"
                @change="saveDemoNoticePreference"
              />
              <span>Більше не показувати</span>
            </label>
          </div>
        </div>
        <template v-slot:action="{ attrs }">
          <button
            type="button"
            class="student-lesson__demo-toast-action"
            v-bind="attrs"
            @click="requestFullAccess"
          >
            Залишити контакти
          </button>
          <button
            type="button"
            class="student-lesson__demo-toast-close"
            v-bind="attrs"
            aria-label="Закрити повідомлення"
            @click="demoLessonNotice = false"
          >
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </template>
      </v-snackbar>
    </div>
  </v-main>
</template>

<script>
import popupEnum from '@/enums/popupEnum';
import statesEnum from '@/enums/statesEnum';
import EvaluationDeck from '@/components/Base/EvaluationDeck/EvaluationDeck';
import GoogleAnaliticService from '@/services/GoogleAnaliticService';

const DEMO_NOTICE_STORAGE_KEY = 'hideDemoLessonNotice';

export default {
  name: 'CurrentLessonPage',
  components: { EvaluationDeck },
  data: () => ({
    time: new Date(),
    ready: false,
    lessonDrawerOpen: false,
    lessonSearch: '',
    drawerAccessFilter: 'all',
    showScrollTop: false,
    demoLessonNotice: false,
    suppressDemoLessonNotice: false,
  }),
  computed: {
    isParentPreview() {
      return Boolean(this.$route.meta && this.$route.meta.parentPreview);
    },
    activeChildId() {
      return this.$store.getters['ParentStore/getActiveChildId'];
    },
    returnSource() {
      return this.$route.query.from || "";
    },
    returnLabel() {
      const labels = {
        "activity-center": "Назад до активності",
        diary: "Назад до щоденника",
        "parent-course": "Назад до предмета",
      };
      return labels[this.returnSource] || "";
    },
    hasUnreadMessages() {
      return this.$store.getters['ChatStore/hasUnreadMessages'];
    },
    needShowChangeStatus() {
      return this.status === 0;
    },
    showEvaluation() {
      return this.evaluationDeck && this.evaluationDeck.hasEvaluations();
    },
    evaluationResult() {
      return this.$store.getters['CurrentLessonStore/getEvaluationResult'];
    },
    isGuest() {
      return this.$store.getters['UserStore/isGuest']();
    },
    isLessonPreview() {
      return this.$store.getters['CurrentLessonStore/isPreview'];
    },
    isExam() {
      return this.$store.getters['CurrentLessonStore/isExam'];
    },
    status() {
      return this.$store.getters['CurrentLessonStore/status'];
    },
    lessonText() {
      return this.$store.getters['CurrentLessonStore/getLessonText'];
    },
    teacher() {
      return this.$store.getters['CurrentLessonStore/getTeacher'];
    },
    teacherName() {
      return [this.teacher.firstName, this.teacher.lastName]
        .filter(Boolean)
        .join(' ');
    },
    videoLinks() {
      return this.$store.getters['CurrentLessonStore/getVideoLinks'];
    },
    nameLesson() {
      return this.$store.getters['CurrentLessonStore/getNameLesson'];
    },
    courseName() {
      return this.$store.getters['CurrentLessonStore/getCourseName'];
    },
    courseId() {
      return (
        this.$store.getters['CurrentLessonStore/getCourseId'] ||
        this.$route.query.courseId
      );
    },
    lessons() {
      return this.$store.getters['LessonsStore/getLessons'];
    },
    currentLesson() {
      return this.lessons.find(
        (lesson) => String(lesson.id) === String(this.getLessonId())
      );
    },
    availableDemoLesson() {
      return this.lessons.find((lesson) => lesson.isDemo) || null;
    },
    filteredDrawerLessons() {
      const query = this.lessonSearch.toLocaleLowerCase('uk');
      return this.lessons.filter((lesson) => {
        const matchesAccess =
          this.drawerAccessFilter === 'all' || lesson.isDemo;
        const matchesSearch =
          !query ||
          `${lesson.index + 1} ${lesson.name}`
            .toLocaleLowerCase('uk')
            .includes(query);
        return matchesAccess && matchesSearch;
      });
    },
    drawerAccessFilters() {
      return [
        { value: 'all', label: 'Усі' },
        { value: 'demo', label: 'Доступні в демо' },
      ];
    },
    currentLessonIndex() {
      return this.lessons.findIndex(
        (lesson) => String(lesson.id) === String(this.getLessonId())
      );
    },
    previousLesson() {
      return this.currentLessonIndex > 0
        ? this.lessons[this.currentLessonIndex - 1]
        : null;
    },
    nextLesson() {
      const nextIndex = this.currentLessonIndex + 1;
      return this.currentLessonIndex !== -1 && nextIndex < this.lessons.length
        ? this.lessons[nextIndex]
        : null;
    },
    previousLessonName() {
      return this.previousLesson ? this.previousLesson.name : 'Це перший урок';
    },
    nextLessonName() {
      return this.nextLesson ? this.nextLesson.name : 'Це останній урок';
    },
    evaluationDeck() {
      return this.$store.getters['CurrentLessonStore/getEvaluationDeck'];
    },
    grade() {
      return this.$store.getters['CurrentLessonStore/getGrade'];
    },
    chatId() {
      return this.$store.getters['CurrentLessonStore/getChatId'];
    },
    lessonOrder() {
      const match = String(this.nameLesson || '').match(/^\s*(\d+)[.)]/);
      return match ? match[1] : this.$route.params.lessonId;
    },
    statusLabel() {
      if (this.status === null || this.status === undefined) return 'Не розпочато';
      const labels = {
        0: 'Не розпочато',
        1: 'На вивченні',
        2: 'На перевірці',
        3: 'Зараховано',
        4: 'Треба доробити',
      };
      return labels[this.status] || 'У роботі';
    },
    statusTheme() {
      const themes = {
        0: 'neutral',
        1: 'progress',
        2: 'check',
        3: 'done',
        4: 'rework',
      };
      return themes[this.status] || 'neutral';
    },
  },
  async created() {
    this.loadDemoNoticePreference();
    window.addEventListener('keydown', this.handleDrawerKeydown);
    window.addEventListener('scroll', this.handlePageScroll, { passive: true });
    this.handlePageScroll();
    await this.loadCurrentLesson();
  },
  destroyed() {
    window.removeEventListener('keydown', this.handleDrawerKeydown);
    window.removeEventListener('scroll', this.handlePageScroll);
    if (this.isParentPreview || typeof this.nameLesson !== 'string') return;
    const spendTimeMs = this.time
      ? new Date().getTime() - this.time.getTime()
      : 0;
    const minutes = Math.floor(spendTimeMs / 1000 / 60) % 60;
    GoogleAnaliticService.trackUserAction(
      this.$store,
      `Провів у уроці: ${this.nameLesson} ${minutes}хв`
    );
  },
  watch: {
    nameLesson(value) {
      if (value && !this.isParentPreview) GoogleAnaliticService.trackUserAction(this.$store, value);
    },
    '$route.params.lessonId'(lessonId, previousLessonId) {
      if (lessonId && lessonId !== previousLessonId) {
        this.ready = false;
        this.loadCurrentLesson();
      }
    },
  },
  methods: {
    returnToSource() {
      if (this.returnSource === 'activity-center') {
        const allowedKeys = ['period', 'type', 'status', 'subject', 'search', 'date', 'page', 'pageSize'];
        const query = allowedKeys.reduce((result, key) => {
          if (this.$route.query[key] !== undefined) result[key] = this.$route.query[key];
          return result;
        }, {});
        this.$router.push({ name: statesEnum.ACTIVITY_CENTER, query });
        return;
      }
      if (this.returnSource === 'diary') {
        const query = {};
        ['view', 'date'].forEach((key) => {
          if (this.$route.query[key] !== undefined) query[key] = this.$route.query[key];
        });
        this.$router.push({ name: statesEnum.PARENT_DIARY, query });
        return;
      }
      if (this.returnSource === 'parent-course') {
        const courseId = this.$route.query.courseId || this.courseId;
        if (!courseId) return;
        this.$router.push({
          name: statesEnum.PARENT_COURSE,
          params: { courseId: String(courseId) },
          query: { period: this.$route.query.period || 'school_year' },
        });
      }
    },
    handlePageScroll() {
      this.showScrollTop = window.pageYOffset > 500;
    },
    scrollToTop() {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    },
    openLessonDrawer() {
      this.lessonDrawerOpen = true;
    },
    closeLessonDrawer() {
      this.lessonDrawerOpen = false;
    },
    handleDrawerKeydown(event) {
      if (event.key === 'Escape' && this.lessonDrawerOpen) {
        this.closeLessonDrawer();
      }
    },
    drawerLessonCount(filter) {
      if (filter === 'demo')
        return this.lessons.filter((lesson) => lesson.isDemo).length;
      return this.lessons.length;
    },
    isCurrentLesson(lesson) {
      return String(lesson.id) === String(this.getLessonId());
    },
    selectDrawerLesson(lesson) {
      if (this.isCurrentLesson(lesson)) {
        this.closeLessonDrawer();
        return;
      }
      this.closeLessonDrawer();
      this.navigateLesson(lesson);
    },
    async loadCurrentLesson() {
      const lessonId = this.getLessonId();
      this.time = new Date();
      try {
        const listedLesson = this.lessons.find(
          (lesson) => String(lesson.id) === String(lessonId)
        );
        const hasPreparedPreview =
          this.isGuest &&
          this.$route.query.preview === '1' &&
          typeof this.nameLesson === 'string' &&
          Boolean(this.nameLesson);
        if (
          (listedLesson && this.isPreviewLesson(listedLesson)) ||
          hasPreparedPreview
        ) {
          await this.$store.dispatch('CurrentLessonStore/setLessonPreview', {
            name: listedLesson ? listedLesson.name : this.nameLesson,
            courseName:
              this.$store.getters['LessonsStore/getCourseName'] || this.courseName,
            courseId: this.$route.query.courseId || this.courseId,
            gradeNumber:
              (listedLesson && listedLesson.gradeNumber) || this.grade,
          });
          return;
        }
        const lessonLoaded = await this.$store.dispatch('CurrentLessonStore/getLesson', {
          lessonId,
          parentPreview: this.isParentPreview,
          childId: this.activeChildId,
        });
        if (!lessonLoaded || String(lessonId) !== String(this.getLessonId())) {
          return;
        }
        if (this.isParentPreview) {
          const loadedCourseId = this.$store.getters['LessonsStore/getCurrentCourseId'];
          if (this.courseId && (
            String(loadedCourseId) !== String(this.courseId) || !this.lessons.length
          )) {
            await this.$store.dispatch('LessonsStore/loadParentLessons', {
              childId: this.activeChildId,
              courseId: this.courseId,
            });
          }
          return;
        }
        const hasCurrentLesson = this.lessons.some(
          (lesson) => String(lesson.id) === String(this.getLessonId())
        );
        if (this.courseId && !hasCurrentLesson) {
          await this.$store.dispatch('LessonsStore/loadLessons', {
            courseId: this.courseId,
          });
        }
      } finally {
        if (String(lessonId) === String(this.getLessonId())) {
          this.ready = true;
          this.demoLessonNotice =
            this.isGuest &&
            !this.isLessonPreview &&
            !this.suppressDemoLessonNotice;
          this.$nextTick(() => {
            this.prepareLessonContent();
          });
        }
      }
    },
    async navigateLesson(lesson) {
      if (!lesson) return;
      const isPreview = this.isPreviewLesson(lesson);
      if (isPreview) {
        await this.$store.dispatch('CurrentLessonStore/setLessonPreview', {
          name: lesson.name,
          courseName: this.courseName,
          courseId: this.courseId,
          gradeNumber: lesson.gradeNumber || this.grade,
        });
      }
      const { preview, ...currentQuery } = this.$route.query;
      const query = this.courseId
        ? {
          ...(this.isParentPreview ? currentQuery : {}),
          courseId: this.courseId,
        }
        : currentQuery;
      try {
        await this.$router.push({
          name: this.isParentPreview ? statesEnum.PARENT_LESSON : statesEnum.LESSON,
          params: { lessonId: lesson.id },
          query: isPreview ? { ...query, preview: '1' } : query,
        });
      } catch (error) {
        if (!error || error.name !== 'NavigationDuplicated') throw error;
      }
    },
    isPreviewLesson(lesson) {
      return Boolean(this.isGuest && lesson && !lesson.isDemo);
    },
    openDemoRequest(lesson) {
      this.$store.dispatch('PopupStore/openPopup', {
        popupName: popupEnum.DEMO_REQUEST_POPUP,
        popupContext: {
          courseName: this.courseName,
          lessonId: lesson.id,
          lessonName: lesson.name,
        },
      });
    },
    requestFullAccess() {
      this.openDemoRequest(
        this.currentLesson || {
          id: this.getLessonId(),
          name: this.nameLesson,
        }
      );
    },
    loadDemoNoticePreference() {
      try {
        this.suppressDemoLessonNotice =
          window.localStorage.getItem(DEMO_NOTICE_STORAGE_KEY) === 'true';
      } catch (error) {
        this.suppressDemoLessonNotice = false;
      }
    },
    saveDemoNoticePreference() {
      try {
        window.localStorage.setItem(
          DEMO_NOTICE_STORAGE_KEY,
          String(this.suppressDemoLessonNotice)
        );
      } catch (error) {
        // The preference remains active for the current page when storage is unavailable.
      }
    },
    goToCourse() {
      if (this.isParentPreview) {
        if (this.courseId) {
          this.$router.push({
            name: statesEnum.PARENT_COURSE,
            params: { courseId: `${this.courseId}` },
            query: { period: this.$route.query.period || 'school_year' },
          });
        } else {
          this.$router.go(-1);
        }
        return;
      }
      if (this.courseId) {
        this.$router.push({
          name: statesEnum.CLASS,
          query: { courseId: this.courseId },
        });
      } else {
        this.$router.go(-1);
      }
    },
    changeLessonStatus() {
      this.$store.dispatch('CurrentLessonStore/changeLessonStatus', {
        lessonId: this.getLessonId(),
      });
    },
    getLessonId() {
      return this.$route.params.lessonId;
    },
    openChat() {
      this.$store.dispatch('PopupStore/openPopup', {
        popupName: popupEnum.CHAT_POPUP,
        popupContext: { chatId: this.chatId },
      });
    },
    prepareLessonContent() {
      const lessonPage = this.$refs.lessonPage;
      if (!lessonPage) return;
      const serverUrl = this.$store.getters['ConfigStore/getServerUrl'];

      Array.from(lessonPage.querySelectorAll('p, div'))
        .reverse()
        .forEach((element) => {
          const text = element.textContent.replace(/\u00a0/g, '').trim();
          const hasContent = element.querySelector(
            'img, iframe, video, audio, table, embed, object, hr, input, button'
          );
          if (!text && !hasContent) element.remove();
        });

      lessonPage.querySelectorAll('img').forEach((image) => {
        const src = image.getAttribute('src');
        if (!src || /^https?:/i.test(src)) return;
        image.setAttribute('src', new URL(src, serverUrl).href);
      });
    },
  },
};
</script>

<style lang="less" scoped>
.student-lesson {
  background: var(--theme-app-background-accent);
  min-height: 100vh;
  transition: background-color 0.2s ease;
}
.student-lesson__inner {
  margin: 0 auto;
  max-width: 1480px;
  padding: 30px 28px 60px;
}
.student-lesson__loading {
  align-items: center;
  color: #667085;
  display: flex;
  flex-direction: column;
  justify-content: center;
  min-height: calc(100vh - 64px);
}
.student-lesson__loading p {
  margin-top: 16px;
}
.student-lesson__context-back {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--theme-banner-text);
  cursor: pointer;
  display: inline-flex;
  font-size: 15px;
  font-weight: 800;
  gap: 6px;
  margin: 0 0 14px;
  padding: 0;
}
.student-lesson__context-back:hover { color: var(--theme-header-background); }
.student-lesson__context-back::v-deep .v-icon,
.student-lesson__breadcrumb button::v-deep .v-icon {
  color: currentColor !important;
}
.student-lesson__context-back:focus-visible {
  border-radius: 5px;
  outline: 3px solid rgba(45,104,189,.2);
  outline-offset: 4px;
}
.student-lesson__breadcrumb {
  align-items: center;
  color: #667085;
  display: flex;
  flex-wrap: nowrap;
  font-size: 16px;
  gap: 11px;
  margin-bottom: 20px;
  min-width: 0;
  width: 100%;
}
.student-lesson__breadcrumb button {
  align-items: center;
  background: transparent;
  border: 0;
  color: var(--theme-banner-text);
  cursor: pointer;
  display: flex;
  font: inherit;
  font-weight: 800;
  gap: 6px;
  padding: 0;
}
.student-lesson__breadcrumb .student-lesson__breadcrumb-current {
  color: #667085;
  display: block;
  flex: 1 1 auto;
  font-weight: 500;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.student-lesson__breadcrumb .student-lesson__breadcrumb-course {
  color: #667085;
  font-weight: 500;
}
.student-lesson__breadcrumb .student-lesson__breadcrumb-course--parent {
  color: var(--theme-banner-text);
  font-weight: 800;
}
.student-lesson__breadcrumb .student-lesson__breadcrumb-course:hover,
.student-lesson__breadcrumb .student-lesson__breadcrumb-course:hover {
  color: var(--theme-header-background);
  text-decoration: underline;
}
.student-lesson__breadcrumb .student-lesson__breadcrumb-course:focus-visible {
  border-radius: 4px;
  outline: 3px solid rgba(45, 104, 189, 0.2);
  outline-offset: 3px;
}
.student-lesson__hero {
  align-items: center;
  background: linear-gradient(115deg, #fff, #f3faf7);
  border: 1px solid #dcebe4;
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.07);
  display: flex;
  gap: 24px;
  justify-content: space-between;
  margin-bottom: 22px;
  padding: 23px 24px;
}
.student-lesson__identity {
  align-items: flex-start;
  display: flex;
  gap: 17px;
  min-width: 0;
}
.student-lesson__icon {
  align-items: center;
  background: #eef4fc;
  border-radius: 14px;
  display: flex;
  flex: 0 0 auto;
  height: 58px;
  justify-content: center;
  width: 58px;
}
.student-lesson__eyebrow {
  color: #2d68bd;
  font-size: 12px;
  font-weight: 850;
}
.student-lesson__identity h1 {
  color: #172033;
  font-size: 27px;
  line-height: 1.3;
  margin: 5px 0 10px;
}
.student-lesson__title-link {
  background: transparent;
  border: 0;
  color: inherit;
  cursor: pointer;
  font: inherit;
  padding: 0;
  text-align: left;
}
.student-lesson__title-link:hover {
  color: #2d68bd;
  text-decoration: underline;
}
.student-lesson__title-link:focus-visible {
  border-radius: 5px;
  outline: 3px solid rgba(45, 104, 189, 0.2);
  outline-offset: 4px;
}
.student-lesson__badges {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.student-lesson__badges > span {
  background: #f0f2f4;
  border-radius: 999px;
  color: #667085;
  font-size: 11px;
  font-weight: 800;
  padding: 5px 9px;
}
.student-lesson__badges .student-lesson__grade {
  background: #eaf6f1;
  color: #176d50;
}
.student-lesson__status--progress {
  background: #eef4fc !important;
  color: #2d68bd !important;
}
.student-lesson__status--check {
  background: #fff3df !important;
  color: #a55d00 !important;
}
.student-lesson__status--done {
  background: #e8f6ee !important;
  color: #207149 !important;
}
.student-lesson__status--rework {
  background: #fff0dd !important;
  color: #a55400 !important;
}
.student-lesson__contact {
  align-items: center;
  display: flex;
  flex: 0 0 auto;
  gap: 14px;
}
.student-lesson__teacher {
  align-items: center;
  border-left: 1px solid var(--theme-border-decorative);
  display: flex;
  gap: 11px;
  padding-left: 22px;
}
.student-lesson__teacher > span {
  align-items: center;
  background: var(--theme-surface-decorative);
  border-radius: 10px;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}
.student-lesson__teacher > span::v-deep .v-icon {
  color: var(--theme-banner-text) !important;
}
.student-lesson__teacher small,
.student-lesson__teacher strong,
.student-lesson__teacher a,
.student-lesson__teacher-demo {
  display: block;
}
.student-lesson__teacher small {
  color: #7a8592;
}
.student-lesson__teacher strong {
  color: #172033;
  font-size: 14px;
  margin: 1px 0;
}
.student-lesson__teacher a {
  color: #2d68bd;
  font-size: 12px;
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.student-lesson__teacher-demo {
  color: #667085;
  font-size: 12px;
  margin-top: 2px;
}
.student-lesson__chat,
.student-lesson__course-content {
  align-items: center;
  background: #fff;
  border: 1px solid var(--theme-accent-decorative);
  border-radius: 10px;
  color: var(--theme-banner-text);
  cursor: pointer;
  display: flex;
  font-weight: 800;
  gap: 7px;
  min-height: 43px;
  padding: 9px 14px;
  position: relative;
}
.student-lesson__course-content {
  border-color: var(--theme-border-decorative);
}
.student-lesson__chat::v-deep .v-icon,
.student-lesson__course-content::v-deep .v-icon {
  color: currentColor !important;
}
.student-lesson__chat:hover,
.student-lesson__course-content:hover {
  background: var(--theme-surface-decorative);
  border-color: var(--theme-accent-decorative);
  color: var(--theme-header-background);
}
.student-lesson__unread {
  background: #df3f35;
  border: 2px solid #fff;
  border-radius: 50%;
  height: 10px;
  position: absolute;
  right: -3px;
  top: -3px;
  width: 10px;
}
.student-lesson__pager {
  align-items: stretch;
  background: #fff;
  border: 1px solid #e4e8eb;
  border-radius: 14px;
  box-shadow: 0 5px 20px rgba(31, 41, 55, 0.05);
  display: grid;
  gap: 10px;
  grid-template-columns: 1fr 1fr;
  margin-bottom: 16px;
  max-width: none;
  padding: 9px;
  width: 100%;
}
.student-lesson__pager--bottom {
  margin-bottom: 0;
  margin-top: 4px;
}
.student-lesson__pager button {
  align-items: center;
  background: #f8faf9;
  border: 1px solid transparent;
  border-radius: 10px;
  color: #344054;
  cursor: pointer;
  display: flex;
  font: inherit;
  font-size: 13px;
  font-weight: 750;
  gap: 10px;
  min-height: 52px;
  min-width: 0;
  padding: 7px 11px;
  text-align: left;
}
.student-lesson__pager button:last-child {
  justify-content: flex-end;
  text-align: right;
}
.student-lesson__pager button:last-child span {
  margin-left: auto;
  text-align: right;
}
.student-lesson__pager button:hover:not(:disabled) {
  background: var(--theme-surface-decorative);
  border-color: var(--theme-accent-decorative);
  color: var(--theme-banner-text);
}
.student-lesson__pager button:hover:not(:disabled) span {
  color: inherit;
}
.student-lesson__pager button:disabled {
  cursor: default;
  opacity: 0.42;
}
.student-lesson__pager button span {
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.student-lesson__pager button small {
  color: #7a8592;
  display: block;
  font-size: 10px;
  font-weight: 650;
  margin-bottom: 2px;
}
.student-lesson__pager button small em {
  background: #f2f0fb;
  border-radius: 999px;
  color: #67569a;
  font-style: normal;
  font-weight: 800;
  margin-left: 5px;
  padding: 2px 6px;
}
.student-lesson__pager::v-deep .v-icon {
  color: inherit;
  flex: 0 0 auto;
}
.student-lesson__drawer-backdrop {
  background: rgba(18, 28, 39, 0.38);
  inset: 0;
  opacity: 0;
  pointer-events: none;
  position: fixed;
  transition: opacity 0.22s;
  z-index: 60;
}
.student-lesson__drawer-backdrop--open {
  opacity: 1;
  pointer-events: auto;
}
.student-lesson__drawer {
  background: var(--theme-app-background-accent);
  bottom: 0;
  box-shadow: -20px 0 50px rgba(20, 31, 45, 0.2);
  display: flex;
  flex-direction: column;
  max-width: 100vw;
  padding: 0;
  position: fixed;
  right: 0;
  top: 0;
  transform: translateX(101%);
  transition: transform 0.25s ease;
  width: 500px;
  z-index: 61;
}
.student-lesson__drawer--open {
  transform: translateX(0);
}
.student-lesson__drawer-header {
  align-items: flex-start;
  background: #fff;
  border-bottom: 1px solid var(--theme-border-decorative);
  display: flex;
  gap: 18px;
  justify-content: space-between;
  padding: 24px;
}
.student-lesson__drawer-header > div > span {
  color: var(--theme-banner-text);
  font-size: 11px;
  font-weight: 850;
  letter-spacing: 0.06em;
  text-transform: uppercase;
}
.student-lesson__drawer-header h2 {
  color: #172033;
  font-size: 23px;
  line-height: 1.25;
  margin: 4px 0 5px;
}
.student-lesson__drawer-header p {
  color: #7a8592;
  font-size: 13px;
  margin: 0;
}
.student-lesson__drawer-header > button {
  align-items: center;
  background: #f2f4f5;
  border: 0;
  border-radius: 10px;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  height: 40px;
  justify-content: center;
  width: 40px;
}
.student-lesson__drawer-header > button:hover {
  background: var(--theme-surface-decorative);
}
.student-lesson__drawer-search {
  align-items: center;
  background: #fff;
  border: 1px solid #dce3e0;
  border-radius: 11px;
  display: flex;
  gap: 9px;
  margin: 16px 18px 10px;
  min-height: 46px;
  padding: 0 12px;
}
.student-lesson__drawer-search:focus-within {
  border-color: var(--theme-accent-decorative);
  box-shadow: 0 0 0 3px var(--theme-surface-decorative);
}
.student-lesson__drawer-search input {
  background: transparent;
  border: 0;
  color: #172033;
  flex: 1;
  font: inherit;
  min-width: 0;
  outline: 0;
}
.student-lesson__drawer-search > button {
  align-items: center;
  background: transparent;
  border: 0;
  cursor: pointer;
  display: flex;
  padding: 2px;
}
.student-lesson__drawer-filters {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin: 0 18px 10px;
}
.student-lesson__drawer-filters > button {
  align-items: center;
  background: #fff;
  border: 1px solid #dfe5e2;
  border-radius: 999px;
  color: #596574;
  cursor: pointer;
  display: flex;
  font-size: 12px;
  font-weight: 800;
  gap: 6px;
  min-height: 34px;
  padding: 6px 10px;
}
.student-lesson__drawer-filters > button:hover {
  border-color: var(--theme-accent-decorative);
}
.student-lesson__drawer-filters > button small {
  align-items: center;
  background: #f2f4f5;
  border-radius: 999px;
  display: flex;
  font-size: 10px;
  height: 20px;
  justify-content: center;
  min-width: 20px;
  padding: 0 5px;
}
.student-lesson__drawer-filters
  .student-lesson__drawer-filter--active {
  background: var(--theme-surface-decorative);
  border-color: var(--theme-accent-decorative);
  color: var(--theme-banner-text);
}
.student-lesson__drawer-filters
  .student-lesson__drawer-filter--active small {
  background: var(--theme-banner-background);
  color: var(--theme-banner-text);
}
.student-lesson__drawer-list {
  display: flex;
  flex: 1;
  flex-direction: column;
  gap: 7px;
  overflow-y: auto;
  padding: 8px 18px 24px;
  scrollbar-color: #91bbaa transparent;
  scrollbar-width: thin;
}
.student-lesson__drawer-list::-webkit-scrollbar {
  width: 6px;
}
.student-lesson__drawer-list::-webkit-scrollbar-track {
  background: transparent;
}
.student-lesson__drawer-list::-webkit-scrollbar-thumb {
  background: #91bbaa;
  border-radius: 999px;
}
.student-lesson__drawer-lesson {
  align-items: center;
  background: #fff;
  border: 1px solid #e4e8e6;
  border-radius: 12px;
  color: #596574;
  cursor: pointer;
  display: flex;
  flex: 0 0 auto;
  gap: 11px;
  min-height: 64px;
  padding: 9px 11px;
  text-align: left;
  width: 100%;
}
.student-lesson__drawer-lesson:hover {
  border-color: var(--theme-accent-decorative);
  box-shadow: 0 4px 13px rgba(31, 41, 55, 0.06);
}
.student-lesson__drawer-lesson--active {
  background: var(--theme-surface-decorative);
  border-color: var(--theme-accent-decorative);
  color: var(--theme-banner-text);
}
.student-lesson__drawer-number {
  align-items: center;
  background: #f0f3f4;
  border-radius: 9px;
  display: flex;
  flex: 0 0 auto;
  font-size: 12px;
  font-weight: 900;
  height: 36px;
  justify-content: center;
  width: 36px;
}
.student-lesson__drawer-lesson--active .student-lesson__drawer-number {
  background: var(--theme-banner-background);
  color: var(--theme-banner-text);
}
.student-lesson__drawer-lesson--active .student-lesson__drawer-name strong {
  color: var(--theme-banner-text);
}
.student-lesson__drawer-lesson--preview {
  background: #fcfbff;
}
.student-lesson__drawer-lesson--preview:hover {
  background: #f7f4fc;
}
.student-lesson__drawer-name {
  flex: 1;
  min-width: 0;
}
.student-lesson__drawer-name strong,
.student-lesson__drawer-name small {
  display: block;
}
.student-lesson__drawer-name strong {
  color: #344054;
  font-size: 13px;
  line-height: 1.3;
}
.student-lesson__drawer-name small {
  color: #7a8592;
  font-size: 10px;
  margin-top: 4px;
}
.student-lesson__drawer-name .student-lesson__drawer-availability {
  color: #67569a;
  font-weight: 800;
}
.student-lesson__drawer-name
  .student-lesson__drawer-availability--demo {
  color: #207149;
}
.student-lesson__drawer-empty {
  align-items: center;
  color: #7a8592;
  display: flex;
  flex-direction: column;
  gap: 5px;
  justify-content: center;
  min-height: 240px;
  text-align: center;
}
.student-lesson__drawer-empty strong {
  color: #344054;
  margin-top: 5px;
}
.student-lesson__scroll-top {
  align-items: center;
  background: var(--theme-header-background);
  border: 1px solid rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  bottom: 24px;
  box-shadow: 0 8px 24px var(--theme-border-decorative);
  color: var(--theme-header-text);
  cursor: pointer;
  display: flex;
  height: 48px;
  justify-content: center;
  position: fixed;
  right: 24px;
  transition: background 0.15s, box-shadow 0.15s, transform 0.15s;
  width: 48px;
  z-index: 20;
}
.student-lesson__scroll-top::v-deep .v-icon {
  color: currentColor !important;
}
.student-lesson__scroll-top:hover {
  filter: brightness(0.9);
  box-shadow: 0 10px 28px var(--theme-border-decorative);
  transform: translateY(-2px);
}
.student-lesson__scroll-top:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 3px;
}
.student-scroll-top-enter-active,
.student-scroll-top-leave-active {
  transition: opacity 0.18s, transform 0.18s;
}
.student-scroll-top-enter,
.student-scroll-top-leave-to {
  opacity: 0;
  transform: translateY(10px);
}
.student-lesson__demo-toast-content {
  align-items: center;
  display: flex;
  gap: 11px;
  line-height: 1.4;
}
.student-lesson__demo-toast-content > span {
  align-items: center;
  background: var(--theme-surface-decorative);
  border-radius: 10px;
  display: flex;
  flex: 0 0 auto;
  height: 40px;
  justify-content: center;
  width: 40px;
}
.student-lesson__demo-toast-content::v-deep .v-icon {
  color: var(--theme-header-background);
}
.student-lesson__demo-toast-content strong {
  color: #172033;
  display: block;
  font-size: 14px;
}
.student-lesson__demo-toast-content p {
  color: #667085;
  font-size: 12px;
  margin: 2px 0 0;
}
.student-lesson__demo-toast-preference {
  align-items: center;
  color: #596574;
  cursor: pointer;
  display: inline-flex;
  font-size: 11px;
  font-weight: 700;
  gap: 6px;
  margin-top: 7px;
}
.student-lesson__demo-toast-preference input {
  accent-color: var(--theme-header-background);
  height: 15px;
  margin: 0;
  width: 15px;
}
.student-lesson__demo-toast-preference:focus-within {
  border-radius: 4px;
  outline: 2px solid var(--theme-accent-decorative);
  outline-offset: 2px;
}
.student-lesson__demo-toast::v-deep .v-snack__wrapper {
  background: #fff !important;
  border: 1px solid var(--theme-border-decorative);
  border-left: 4px solid var(--theme-header-background);
  border-radius: 13px;
  box-shadow: 0 12px 34px rgba(23, 32, 51, .2);
  color: #172033;
  max-width: 720px;
}
.student-lesson__demo-toast::v-deep .v-snack__content {
  padding: 13px 15px;
}
.student-lesson__demo-toast-action {
  background: var(--theme-header-background);
  border: 0;
  border-radius: 8px;
  color: #fff;
  font-size: 12px;
  font-weight: 800;
  min-height: 36px;
  padding: 7px 11px;
  white-space: nowrap;
}
.student-lesson__demo-toast-close {
  align-items: center;
  background: transparent;
  border: 0;
  display: flex;
  height: 36px;
  justify-content: center;
  margin-left: 3px;
  width: 36px;
}
.student-lesson__demo-toast-close::v-deep .v-icon {
  color: #667085 !important;
}
.student-lesson__content-card,
.student-lesson__video-card,
.student-lesson__evaluation {
  background: var(--surface-primary);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  margin-bottom: 22px;
  min-width: 0;
  padding: 30px;
}
.student-lesson__content-card {
  overflow: hidden;
}
.student-lesson__preview-card {
  align-items: center;
  background: #fff;
  border: 1px solid var(--theme-border-decorative);
  border-radius: 18px;
  box-shadow: 0 8px 30px rgba(31, 41, 55, 0.06);
  display: flex;
  flex-direction: column;
  margin-bottom: 22px;
  padding: 64px 28px;
  text-align: center;
}
.student-lesson__preview-icon {
  align-items: center;
  background: var(--theme-banner-background);
  border-radius: 18px;
  display: flex;
  height: 68px;
  justify-content: center;
  margin-bottom: 17px;
  width: 68px;
}
.student-lesson__preview-trigger {
  align-items: center;
  background: transparent;
  border: 0;
  border-radius: 16px;
  color: inherit;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  padding: 4px 10px 7px;
}
.student-lesson__preview-trigger:hover .student-lesson__preview-icon {
  box-shadow: 0 8px 20px rgba(31, 41, 55, .13);
  filter: brightness(.96);
  transform: translateY(-2px);
}
.student-lesson__preview-trigger:hover .student-lesson__preview-label {
  color: var(--theme-header-background);
  text-decoration: underline;
}
.student-lesson__preview-trigger:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 3px;
}
.student-lesson__preview-icon::v-deep .v-icon {
  color: var(--theme-header-background);
}
.student-lesson__preview-icon {
  transition: box-shadow .15s, filter .15s, transform .15s;
}
.student-lesson__preview-label {
  color: #67569a;
  font-size: 12px;
  font-weight: 850;
  letter-spacing: .05em;
  text-transform: uppercase;
}
.student-lesson__preview-card h2 {
  color: #172033;
  font-size: 26px;
  margin: 8px 0 10px;
}
.student-lesson__preview-card p {
  color: #667085;
  line-height: 1.6;
  margin: 0 auto 24px;
  max-width: 650px;
}
.student-lesson__preview-submit {
  align-items: center;
  background: var(--theme-header-background);
  border: 0;
  border-radius: 10px;
  color: #fff;
  display: flex;
  font-weight: 800;
  gap: 8px;
  justify-content: center;
  min-height: 48px;
  padding: 10px 18px;
}
.student-lesson__preview-back {
  background: transparent;
  border: 0;
  color: #667085;
  font-size: 13px;
  font-weight: 750;
  margin-top: 14px;
  padding: 5px;
}
.student-lesson__preview-submit:hover { filter: brightness(.92); }
.student-lesson__preview-back:hover { color: var(--theme-header-background); }
.student-lesson__content {
  color: #303b4c;
  font-family: 'Open Sans', sans-serif;
  font-size: 17px;
  line-height: 1.65;
  margin: 0;
  max-width: 100%;
  min-width: 0;
  overflow-wrap: anywhere;
  width: 100%;
  word-break: normal;
}
.student-lesson__content::v-deep * {
  box-sizing: border-box;
  max-width: 100%;
}
.student-lesson__content::v-deep h1,
.student-lesson__content::v-deep h2,
.student-lesson__content::v-deep h3,
.student-lesson__content::v-deep h4,
.student-lesson__content::v-deep h5,
.student-lesson__content::v-deep h6 {
  color: #172033;
  font-family: inherit !important;
  font-weight: 750;
  line-height: 1.3;
  margin: 1.4em 0 0.65em !important;
}
.student-lesson__content::v-deep h1:first-child,
.student-lesson__content::v-deep h2:first-child,
.student-lesson__content::v-deep h3:first-child {
  margin-top: 0 !important;
}
.student-lesson__content::v-deep p {
  font-family: inherit !important;
  line-height: 1.65 !important;
  margin: 0 0 1em !important;
  text-indent: 0 !important;
}
.student-lesson__content::v-deep div[style*='text-indent'],
.student-lesson__content::v-deep span[style*='text-indent'] {
  text-indent: 0 !important;
}
.student-lesson__content::v-deep p:empty {
  display: none;
}
.student-lesson__content::v-deep ul,
.student-lesson__content::v-deep ol {
  margin: 0 0 1.25em;
  padding-left: 1.6em;
}
.student-lesson__content::v-deep li {
  margin-bottom: 0.4em;
}
.student-lesson__content::v-deep img {
  display: block;
  height: auto !important;
  margin: 20px auto;
  max-width: 100% !important;
  object-fit: contain;
}
.student-lesson__content::v-deep iframe,
.student-lesson__content::v-deep video,
.student-lesson__content::v-deep audio,
.student-lesson__content::v-deep embed,
.student-lesson__content::v-deep object {
  max-width: 100% !important;
}
.student-lesson__content::v-deep iframe,
.student-lesson__content::v-deep video {
  display: block;
  margin: 22px auto;
}
.student-lesson__content::v-deep table {
  border-collapse: collapse;
  display: table !important;
  margin: 20px 0;
  max-width: 100% !important;
  overflow: visible !important;
  table-layout: fixed;
  width: 100% !important;
}
.student-lesson__content::v-deep td,
.student-lesson__content::v-deep th {
  height: auto !important;
  max-width: none !important;
  overflow: visible !important;
  padding: 9px 12px;
  vertical-align: top;
  white-space: normal !important;
  word-break: break-word;
}
.student-lesson__content::v-deep td > div,
.student-lesson__content::v-deep th > div {
  height: auto !important;
  max-height: none !important;
  overflow: visible !important;
  width: auto !important;
}
.student-lesson__content::v-deep blockquote {
  background: #f4f8f6;
  border-left: 4px solid #7bbca3;
  margin: 20px 0;
  padding: 14px 18px;
}
.student-lesson__content::v-deep a {
  color: #1976a8;
  overflow-wrap: anywhere;
}
.student-lesson__complete {
  align-items: center;
  background: var(--theme-surface-decorative);
  border: 1px solid var(--theme-border-decorative);
  border-radius: 14px;
  display: flex;
  gap: 13px;
  margin: 30px auto 0;
  max-width: 1080px;
  padding: 16px;
}
.student-lesson__complete > span {
  align-items: center;
  background: var(--theme-banner-background);
  border-radius: 10px;
  display: flex;
  height: 42px;
  justify-content: center;
  width: 42px;
}
.student-lesson__complete > span::v-deep .v-icon {
  color: var(--theme-banner-text) !important;
}
.student-lesson__complete strong {
  color: var(--theme-banner-text);
}
.student-lesson__complete p {
  color: #667085;
  margin: 2px 0 0;
}
.student-lesson__complete button {
  background: var(--theme-header-background);
  border: 0;
  border-radius: 10px;
  color: var(--theme-header-text);
  cursor: pointer;
  font-weight: 850;
  margin-left: auto;
  min-height: 42px;
  padding: 9px 18px;
}
.student-lesson__complete button:hover {
  filter: brightness(0.9);
}
.student-lesson__complete button:focus-visible {
  outline: 3px solid var(--theme-accent-decorative);
  outline-offset: 3px;
}
.student-lesson__video-card h2,
.student-lesson__evaluation header h2 {
  color: #172033;
  font-size: 22px;
  margin: 0 0 16px;
}
.student-lesson__video {
  aspect-ratio: 16/9;
  border-radius: 13px;
  margin: 0 auto;
  max-width: 900px;
  overflow: hidden;
}
.student-lesson__video iframe {
  height: 100%;
  width: 100%;
}
.student-lesson__evaluation header {
  margin-bottom: 18px;
}
.student-lesson__evaluation header h2 {
  color: var(--theme-banner-text);
  margin-bottom: 5px;
}
.student-lesson__evaluation header p {
  color: var(--text-secondary);
  margin: 0;
}
.student-lesson__evaluation::v-deep .container {
  max-width: 100%;
  padding-left: 0;
  padding-right: 0;
}
@media (max-width: 960px) {
  .student-lesson__hero {
    align-items: stretch;
    flex-direction: column;
  }
  .student-lesson__contact {
    justify-content: space-between;
  }
  .student-lesson__teacher {
    border-left: 0;
    padding-left: 0;
  }
}
@media (max-width: 600px) {
  .student-lesson__inner {
    padding: 22px 14px 40px;
  }
  .student-lesson__breadcrumb {
    font-size: 14px;
  }
  .student-lesson__hero {
    padding: 17px;
  }
  .student-lesson__identity h1 {
    font-size: 22px;
  }
  .student-lesson__icon {
    height: 48px;
    width: 48px;
  }
  .student-lesson__contact {
    align-items: stretch;
    flex-direction: column;
  }
  .student-lesson__chat {
    justify-content: center;
    width: 100%;
  }
  .student-lesson__course-content {
    justify-content: center;
    width: 100%;
  }
  .student-lesson__drawer {
    width: 100%;
  }
  .student-lesson__drawer-header {
    padding: 18px;
  }
  .student-lesson__scroll-top {
    bottom: 16px;
    height: 44px;
    right: 16px;
    width: 44px;
  }
  .student-lesson__pager {
    grid-template-columns: 1fr 1fr;
    padding: 7px;
  }
  .student-lesson__pager button {
    min-height: 58px;
    padding: 7px 9px;
  }
  .student-lesson__pager button span {
    white-space: normal;
  }
  .student-lesson__content-card,
  .student-lesson__video-card,
  .student-lesson__evaluation {
    border-radius: 14px;
    padding: 18px 14px;
  }
  .student-lesson__content {
    font-size: 16px;
  }
  .student-lesson__complete {
    align-items: flex-start;
    flex-wrap: wrap;
  }
  .student-lesson__complete button {
    margin-left: 0;
    width: 100%;
  }
}
</style>
